import type { components } from '@fluencelabs/vodopad-admin-api'
import { Alert, Card, Col, Row, Statistic, Typography } from 'antd'
import type { ReactNode } from 'react'

import { formatAmount, formatCount, formatShare } from '@/utils/count'

import { soldShare } from './capacitySummary'
import {
  combineClusters,
  ipUsageSegments,
  usageSegments,
} from './capacityTotals'
import { MeasuredAt } from './MeasuredAt'
import { UsageBar, UsageLegend, type UsageSegment } from './UsageBar'

type Cluster = components['schemas']['ClusterCapacityDto']

function Figure({
  title,
  share,
  free,
  segments,
}: {
  title: string
  share?: number
  free: ReactNode
  segments: UsageSegment[]
}) {
  return (
    <Col xs={24} md={12} xl={6}>
      <Statistic
        title={title}
        value={formatShare(share)}
        suffix={
          <Typography.Text type="secondary" style={{ fontSize: 14 }}>
            sold · {free} free
          </Typography.Text>
        }
      />
      <UsageBar label={title} segments={segments} />
    </Col>
  )
}

/** What every live measured cluster sold and can still sell, before the per-cluster cards. */
export function CapacityOverview({ clusters }: { clusters: Cluster[] }) {
  const { rows, ips, unmeasured, oldestMeasurement } = combineClusters(clusters)
  const ipv4 = ips.find((row) => row.family === 'V4')
  return (
    <Card title="All clusters" style={{ marginTop: 16 }}>
      {oldestMeasurement && (
        <div style={{ marginBottom: 16 }}>
          <MeasuredAt at={oldestMeasurement} prefix="Oldest measurement" />
        </div>
      )}
      {unmeasured.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Not in totals until measured: ${unmeasured.join(', ')}`}
        />
      )}
      <UsageLegend />
      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        {rows.map((row) => (
          <Figure
            key={row.resource}
            title={row.resource}
            share={soldShare(row)}
            free={formatAmount(row.free, 0)}
            segments={usageSegments(row)}
          />
        ))}
        {ipv4 && (
          <Figure
            title="Public IPv4"
            share={
              ipv4.total > 0n
                ? Number(ipv4.sold) / Number(ipv4.total)
                : undefined
            }
            free={formatCount(ipv4.free)}
            segments={ipUsageSegments(ipv4)}
          />
        )}
      </Row>
      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        NVMe and replicated NVMe are two views of the same disks; do not add
        them up.
      </Typography.Paragraph>
    </Card>
  )
}
